import * as React from "react"
import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

// Field Root
export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal" | "responsive"
  "data-invalid"?: boolean
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ className, orientation = "vertical", "data-invalid": dataInvalid, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="group"
        data-orientation={orientation}
        data-invalid={dataInvalid}
        className={cn(
          "space-y-2",
          orientation === "horizontal" && "flex items-start gap-4",
          orientation === "responsive" && "@container/field flex flex-col gap-2 @sm:flex-row @sm:items-start @sm:gap-4",
          dataInvalid && "text-destructive",
          className
        )}
        {...props}
      />
    )
  }
)
Field.displayName = "Field"

// FieldSet
export const FieldSet = React.forwardRef<HTMLFieldSetElement, React.HTMLAttributes<HTMLFieldSetElement>>(
  ({ className, ...props }, ref) => {
    return (
      <fieldset
        ref={ref}
        className={cn("space-y-6", className)}
        {...props}
      />
    )
  }
)
FieldSet.displayName = "FieldSet"

// FieldLegend
export interface FieldLegendProps extends React.HTMLAttributes<HTMLLegendElement> {
  variant?: "legend" | "label"
}

export const FieldLegend = React.forwardRef<HTMLLegendElement, FieldLegendProps>(
  ({ className, variant = "legend", ...props }, ref) => {
    return (
      <legend
        ref={ref}
        className={cn(
          "font-medium",
          variant === "legend" && "text-lg",
          variant === "label" && "text-sm",
          className
        )}
        {...props}
      />
    )
  }
)
FieldLegend.displayName = "FieldLegend"

// FieldGroup
export const FieldGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-6", className)}
        {...props}
      />
    )
  }
)
FieldGroup.displayName = "FieldGroup"

// FieldContent
export const FieldContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col gap-1", className)}
        {...props}
      />
    )
  }
)
FieldContent.displayName = "FieldContent"

// FieldLabel
export interface FieldLabelProps extends React.ComponentPropsWithoutRef<typeof Label> {
  asChild?: boolean
}

export const FieldLabel = React.forwardRef<
  React.ElementRef<typeof Label>,
  FieldLabelProps
>(({ className, ...props }, ref) => {
  return (
    <Label
      ref={ref}
      className={cn("text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", className)}
      {...props}
    />
  )
})
FieldLabel.displayName = "FieldLabel"

// FieldTitle
export const FieldTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("text-sm font-medium leading-none", className)}
        {...props}
      />
    )
  }
)
FieldTitle.displayName = "FieldTitle"

// FieldDescription
export const FieldDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
      />
    )
  }
)
FieldDescription.displayName = "FieldDescription"

// FieldSeparator
export const FieldSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-4", className)}
        {...props}
      >
        <div className="h-px flex-1 bg-border" />
        {children && <span className="text-sm text-muted-foreground">{children}</span>}
        <div className="h-px flex-1 bg-border" />
      </div>
    )
  }
)
FieldSeparator.displayName = "FieldSeparator"

// FieldError
export interface FieldErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {
  errors?: Array<{ message?: string } | undefined>
}

export const FieldError = React.forwardRef<HTMLParagraphElement, FieldErrorProps>(
  ({ className, errors, children, ...props }, ref) => {
    const errorMessages = errors?.filter(Boolean).map(e => e?.message).filter(Boolean) || []
    const body = errorMessages.length > 0 ? errorMessages : children

    if (!body || (Array.isArray(body) && body.length === 0)) {
      return null
    }

    return (
      <p
        ref={ref}
        className={cn("text-sm font-medium text-destructive", className)}
        {...props}
      >
        {Array.isArray(body) && body.length > 1 ? (
          <ul className="list-disc list-inside space-y-1">
            {body.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        ) : (
          Array.isArray(body) ? body[0] : body
        )}
      </p>
    )
  }
)
FieldError.displayName = "FieldError"
